<template>
  <my-select ref="vinTagsRef"
             class="my-vin-tags"
             :model-value="value"
             mode="tags"
             show-search
             :options="options"
             :token-separators="tokenSeparators"
             :max-tag-count="maxTagCount"
             :placeholder="placeholder"
             :search-value="searchValue"
             @search="handleSearch"
             @change="handleChange"
             @blur="flushSearchValue"
             v-bind="$attrs" />
</template>

<script setup>
import {computed, defineEmits, defineProps, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch} from 'vue';

const props = defineProps({
  modelValue: {
    type: [Array, String],
    default: undefined,
  },
  value1: {
    type: [String],
    default: ''
  },
  value2: {
    type: [Array],
    default: () => []
  },
  placeholder: {
    type: String,
    default: '车架号，批量查换行输入',
  },
  maxTagCount: {
    type: [Number, String],
    default: 1,
  },
  max: {
    type: Number,
    default: 200,
  },
});

const emits = defineEmits(['update:modelValue', 'update:value1', 'update:value2']);

const instance = getCurrentInstance();
const hasModelValue = computed(() => {
  const vnodeProps = instance?.vnode?.props || {};
  return Object.prototype.hasOwnProperty.call(vnodeProps, 'modelValue')
    || Object.prototype.hasOwnProperty.call(vnodeProps, 'model-value');
});

const tokenSeparators = [',', '，', '\n', '\t', ' ', ';', '；'];
const vinTagsRef = ref();
const searchValue = ref('');

const value = ref([]);

const normalizeVinList = value => {
  const source = (Array.isArray(value) ? value : [value]).flatMap(item => String(item || '').split(/[\s,，;；]+/));
  return [...new Set(source
    .map(item => String(item || '').trim().toUpperCase())
    .filter(Boolean))]
    .slice(0, props.max);
};

// 重置表单时同步清空 a-select 内部仍显示的临时搜索文本。
watch([() => props.modelValue, () => props.value1, () => props.value2], () => {
  if (hasModelValue.value && Array.isArray(props.modelValue) && props.modelValue.length) {
    value.value = normalizeVinList(props.modelValue);
  } else if (hasModelValue.value && typeof props.modelValue === 'string' && props.modelValue) {
    value.value = normalizeVinList(props.modelValue);
  } else if (props.value1) {
    value.value = normalizeVinList(props.value1);
  } else if (props.value2 && props.value2.length) {
    value.value = normalizeVinList(props.value2);
  } else {
    value.value = [];
    searchValue.value = '';
  }
}, {immediate: true, deep: true});

const options = [];

const handleSearch = value => {
  searchValue.value = value || '';
};

const handleChange = value => {
  //console.log('handleChange', value);
  const nextValue = normalizeVinList(value);
  searchValue.value = '';
  updateValue(nextValue);
};

const updateValue = (value) => {
  //console.log('updateValue', value);
  if (hasModelValue.value) {
    emits('update:modelValue', Array.isArray(props.modelValue) ? value : (value.length <= 1 ? (value[0] || '') : value));
  }
  if (value?.length) {
    if (value.length > 1) {
      emits('update:value1', '');
      emits('update:value2', value);
    } else {
      emits('update:value1', value[0]);
      emits('update:value2', []);
    }
  } else {
    emits('update:value1', '');
    emits('update:value2', []);
  }
}

// 搜索按钮的 click 可能早于 a-select 的 blur，先同步尚未生成标签的输入值。
const flushSearchValue = () => {
  //console.log('flushSearchValue', value);
  if (!searchValue.value) {
    return;
  }

  const nextValue = normalizeVinList([
    ...value.value,
    searchValue.value,
  ]);
  searchValue.value = '';
  updateValue(nextValue);
};

const handlePointerDown = event => {
  const target = event.target instanceof Element ? event.target : null;
  const ownElement = vinTagsRef.value?.$el;
  if (!target || target.closest('.ant-select-dropdown') || (ownElement && ownElement.contains(target))) {
    return;
  }
  flushSearchValue();
};

onMounted(() => {
  document.addEventListener('pointerdown', handlePointerDown, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handlePointerDown, true);
});
</script>

<style scoped>
.my-vin-tags {
  max-width: 200px;
}
</style>
